import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { FaPaperPlane, FaRobot, FaUser, FaArrowLeft, FaCoins, FaTrashAlt } from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';
import { AIConsultantsData } from '../constants/aiAgents';

const AiChatInterface = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [queriesRemaining, setQueriesRemaining] = useState(0);
    const [serviceType, setServiceType] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchChatHistory();
    }, [sessionId]);

    const fetchChatHistory = async () => {
        try {
            const response = await HttpInterceptor.get(`/api/ai/chat/${sessionId}`);
            if (response.data?.success) {
                if (response.data.data.messages) {
                    setMessages(response.data.data.messages);
                    setServiceType(response.data.data.serviceType || '');
                } else {
                    setMessages(response.data.data);
                }
            }

            // We also need queriesRemaining, let's fetch enrollments to get the balance
            const enrollRes = await HttpInterceptor.get('/api/ai/enrollments');
            if (enrollRes.data?.success) {
                // Find which enrollment this session belongs to? 
                // Backend didn't perfectly give it in /chat/sessionId endpoint, but we can check if it exists or use default
                // Actually, for simplicity we just fetch /api/ai/enrollments
                const enrollments = enrollRes.data.data;
                // The endpoint /chat/:sessionId only gives messages. We can find the serviceType implicitly or we could have backend return it. 
                // Let's assume the user has some queries remaining across all.
                const totalRemaining = enrollments.reduce((acc, curr) => curr.queriesRemaining || acc, 0);
                setQueriesRemaining(totalRemaining);
            }
        } catch (error) {
            toast.error('Failed to load chat history');
            console.error(error);
        }
    };

    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim()) return;

        if (queriesRemaining <= 0) {
            toast.error("You are out of free daily queries! Please purchase a top-up.");
            return;
        }

        const newMessage = {
            _id: Date.now().toString(),
            sender: 'user',
            message: inputMessage,
            createdAt: new Date().toISOString()
        };

        setMessages(prev => [...prev, newMessage]);
        setInputMessage('');
        setLoading(true);

        try {
            const response = await HttpInterceptor.post('/api/ai/chat', {
                sessionId,
                message: inputMessage
            });

            if (response.data?.success) {
                const aiMsg = response.data.data.message;
                setMessages(prev => [...prev, aiMsg]);
                setQueriesRemaining(response.data.data.queriesRemaining);
            } else {
                toast.error(response.data?.message || 'Failed to get AI response');
                setMessages(prev => prev.filter(m => m._id !== newMessage._id));
            }
        } catch (error) {
            console.error(error);
            if (error.response?.status === 402) {
                toast.error("Out of free daily queries. Please purchase a top-up.");
                setQueriesRemaining(0);
            } else {
                const apiDetailedError = error.response?.data?.details || error.response?.data?.message || 'Failed to send message';
                toast.error(apiDetailedError);
            }
            setMessages(prev => prev.filter(m => m._id !== newMessage._id));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSession = async () => {
        try {
            const res = await HttpInterceptor.delete(`/api/ai/chat-sessions/${sessionId}`);
            if (res.data?.success) {
                toast.success('Conversation deleted');
                navigate('/student/my-ai');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete session');
        }
    };

    const getConsultantDetails = () => {
        const normalizedType = serviceType?.toLowerCase().replace(/\s+/g, '-');
        const aiAgent = AIConsultantsData.find(ai => ai.type === normalizedType);
        
        if (aiAgent) {
            return {
                name: aiAgent.name.split(' (AI')[0],
                title: aiAgent.service.includes('AI') ? aiAgent.service : `AI ${aiAgent.service}`,
                image: aiAgent.profileImage
            };
        }
        
        return { name: "Consultant", title: "AI Agent", image: "/assets/ai-agents/priya_representative.png" };
    };
    const consultant = getConsultantDetails();

    return (
        <div className="max-w-5xl mx-auto my-2 md:my-4 bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden shadow-xl flex flex-col h-[calc(100vh-160px)] min-h-[500px]">
            {/* Header */}
            <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/student/my-ai')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FaArrowLeft className="text-gray-600" />
                    </button>
                    <div className="flex items-center gap-2">
                        <img src={consultant.image} alt={consultant.name} className="w-10 h-10 rounded-full object-cover shadow-sm border border-purple-200" />
                        <div>
                            <h2 className="font-bold text-gray-800 leading-tight">{consultant.name} <span className="text-sm font-normal text-gray-500 ml-1">({consultant.title})</span></h2>
                            <div className="flex items-center gap-1.5 text-xs text-green-600">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                Online
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={messages.length === 0}
                        className="p-2.5 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-xl transition-all border border-red-100 flex items-center gap-2 text-xs font-bold disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Delete this conversation"
                    >
                        <FaTrashAlt />
                        <span className="hidden sm:inline">Delete Log</span>
                    </button>
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1.5 rounded-full border border-purple-100 text-purple-700 font-medium text-sm">
                        <FaCoins className="text-amber-500" />
                        <span>{queriesRemaining} Daily Queries Left</span>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDeleteSession}
                title="Clear Conversation"
                message="Are you sure you want to clear this entire conversation? This cannot be undone."
                confirmText="Delete History"
                variant="danger"
            />

            {/* Disclaimer Banner */}
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-center text-amber-800 shadow-inner">
                <span className="font-bold">⚠️ CAUTION:</span> AI-generated advice. Not a substitute for professional medical or certified expert diagnosis.
            </div>

            {/* Chat Area */}
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-6">
                {messages.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 space-y-4">
                        <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center shadow-inner p-1">
                            <img src={consultant.image} alt={consultant.name} className="w-full h-full rounded-full object-cover shadow-md" />
                        </div>
                        <p className="text-sm">Start a conversation with {consultant.name}!</p>
                    </div>
                )}

                {messages.map((msg, idx) => {
                    const isAi = msg.sender === 'ai';
                    return (
                        <div key={msg._id || idx} className={`flex ${isAi ? 'justify-start' : 'justify-end'} animate-fade-in-up`}>
                            <div className={`flex max-w-[85%] ${isAi ? 'flex-row' : 'flex-row-reverse'} gap-2 md:gap-3 items-end`}>
                                <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center shadow-sm ${isAi ? 'bg-purple-100' : 'bg-gradient-to-br from-teal-500 to-blue-500 text-white'}`}>
                                    {isAi ? <img src={consultant.image} alt="AI" className="w-8 h-8 rounded-full object-cover shadow-sm border border-purple-200" /> : <FaUser className="w-4 h-4" />}
                                </div>
                                <div className={`px-4 py-2.5 rounded-2xl shadow-sm text-sm md:text-base leading-relaxed whitespace-pre-wrap ${isAi ? 'bg-white text-gray-800 border-gray-100 border rounded-bl-none' : 'bg-gradient-to-br from-teal-500 to-blue-600 text-white rounded-br-none'
                                    }`}>
                                    {msg.message}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {loading && (
                    <div className="flex justify-start animate-fade-in-up">
                        <div className="flex gap-2 items-end">
                            <img src={consultant.image} alt="AI" className="w-8 h-8 rounded-full object-cover shadow-sm border border-purple-200" />
                            <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-none shadow-sm border border-gray-100 flex items-center gap-1.5">
                                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="bg-white border-t p-3 md:p-4">
                <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex gap-2">
                    <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        disabled={loading || queriesRemaining <= 0}
                        placeholder={queriesRemaining <= 0 ? "Out of free queries" : "Type your message..."}
                        className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <button
                        type="submit"
                        disabled={!inputMessage.trim() || loading || queriesRemaining <= 0}
                        className="w-12 h-12 bg-gradient-to-tr from-purple-600 to-indigo-600 text-white rounded-full flex items-center justify-center shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95"
                    >
                        <FaPaperPlane className="w-4 h-4 ml-[-2px]" />
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AiChatInterface;
